<template>
    <div class="map-container">
        <map-container id="routa-material-route-map" ref="mapContainer" :find-user="false" @onMapChanged="mapChanged" @onMapClicked="mapClicked" @on-zoom-level-change="onZoomLevelChange" :center="mapCenter"/>
        <div v-if="loading" id="loader" class="spinner" />
        <div class="col-12 d-flex justify-content-end p-3" style="position: absolute; top: 0; z-index: 5000; pointer-events: none;">
            <button class="btn btn-secondary form-button" style="pointer-events: auto;" @click="reset">{{ $t('common.clear') }}</button>
            <button class="btn btn-danger form-button" style="pointer-events: auto;" @click="undo">{{ $t('common.undo') }}</button>
        </div>
    </div>
</template>

<script>
import MapContainer from "@/components/map/MapContainer";
import { timeUtils } from '@/components/mixins/TimeUtils'
import { mapHelper } from '@/components/mixins/MapMixin'
import { restApi } from '@/components/mixins/RestApiMixin'
import { measureDistanceMixin } from "@/components/mixins/MeasureDistanceMixin";
import {getDistance} from "geolib";
import { inject } from "vue";

export default {
    name: 'RouteCreationMap',
    components: { MapContainer },
    mixins: [timeUtils, mapHelper, restApi, measureDistanceMixin],
    setup() {
        const user = inject('user');
        return { user };
    },
    data: function () {
        return {
            loading: false,
            points: [],
            roadGeometries: [],
            roadGeometry: null,
            roadLength: 0,
            mapCenter: null
        }
    },
    watch: {
        roadGeometry: {
            handler: function () {
                this.$emit('onGeometry', this.roadGeometry)
            },
            deep: true
        }
    },
    mounted() {
        if(this.getMapSettingsIn(['center'])){
            this.mapCenter = this.getMapSettingsIn(['center'])
        } else if (this.user && this.user.company.location) {
            this.mapCenter = {
                lon: this.user.company.location.x,
                lat: this.user.company.location.y,
            }
        } else if (this.center) {
            this.mapCenter = this.center
        }
    },
    methods: {
        close() {
            this.reset()
            this.$emit('close')
        },
        mapChanged(map) {
            this.map = map
            this.map.resizeMap()
        },
        onZoomLevelChange(data) {
            this.$store.commit('storeMapZoomLevel', data)
        },
        mapClicked: function (coord) {
            this.addMeasurePoint(coord)
        },

        reset() {
            if (this.map) {
                try {
                    this.map.removeMapMarkerByType(this.MEASURE_ROAD)
                    this.map.removePolylinesByType(this.MEASURE_ROAD)
                } catch (error) {
                    // Silent error catch and fail
                }
            }
            this.roadGeometries = []
            this.points = []
        },

        undo() {
            if (this.points.length > 0) {
                if (this.roadGeometries.length > 0) {
                    this.map.removePolyline(this.roadGeometries.length-1, this.MEASURE_ROAD)
                    this.roadGeometries.pop()
                }
                // Remove last point
                this.map.removeMapMarker(this.points.length, this.MEASURE_ROAD)
                this.points.pop()

                this.calculateRoadLength()
                // Change remaining last point to draggable
                if (this.points.length > 0) {
                    let icon = this.map.getMarkerStore().getNeutralCircleIcon(this.map.getMapType() === 'LEAFLET')
                    let point = this.points[this.points.length - 1]
                    this.map.showMapMarker(this.points.length, this.MEASURE_ROAD, point.lat, point.lng, icon, true)
                }
            }
        },

        addMeasurePoint(coord) {
            if (coord.id) {
                // Existing point moved
                this.points[coord.id-1] = coord.latlng
            } else {
                this.points.push(coord)
                this.addPoint(coord, this.points.length)
            }
            if (this.points.length > 1) {
                let lastPointIndex = this.points.length-1
                if (coord.id) {
                    // Moving last point
                    this.map.removePolyline(this.roadGeometries.length-1, this.MEASURE_ROAD)
                    this.roadGeometries.pop()
                }
                if (this.points.length === 2) {
                    this.fetchRoad(this.points[0], this.points[1])
                } else {
                    this.fetchRoad(this.points[lastPointIndex], this.points[lastPointIndex-1])
                }
            }
        },

        fetchRoad(startPoint, endPoint) {
            this.loading = true
            this.restFetchParams(this.roadGeometryPointsUrl, {
                startLat: startPoint.lat,
                startLon: startPoint.lng,
                endLat: endPoint.lat,
                endLon: endPoint.lng
            }, this.handleResponse, this.onError)
        },

        handleResponse: function (response) {
            this.loading = false
            if (response && response.data && response.data.geometry) {
                if (response.data.geometry.length > 1) {
                    this.roadGeometries.push(response.data)
                } else {
                    let start = this.points[this.points.length-2]
                    let end = this.points[this.points.length-1]
                    // Use points as geometry
                    this.roadGeometries.push({
                        geometry: [
                            {lat: start.lat, lon: start.lng},
                            {lat: end.lat, lon: end.lng},
                        ],
                        properties: []
                    })
                }
                this.fillGeometryGaps()
                this.calculateRoadLength()
                this.drawLastRoad()
                this.roadGeometry = this.getMergedLine()
            }
        },

        onError() {
            this.showAlert(this.$t('map.error_road_geometry'))
            this.loading = false
            this.reset()
        },

        fillGeometryGaps() {
            if (this.roadGeometries.length > 1) {
                let lineA = this.roadGeometries[this.roadGeometries.length-1]
                let lineB = this.roadGeometries[this.roadGeometries.length-2]
                let lineAStart = lineA.geometry[0]
                let lineAEnd = lineA.geometry[lineA.geometry.length-1]
                let lineBStart = lineB.geometry[0]
                let lineBEnd = lineB.geometry[lineB.geometry.length-1]
                // Connect the nearest points
                let aStartTobStart = getDistance(lineAStart, lineBStart)
                let aEndTobStart = getDistance(lineAEnd, lineBStart)
                let aStartTobEnd = getDistance(lineAStart, lineBEnd)
                let aEndTobEnd = getDistance(lineAEnd, lineBEnd)
                let shortest = Math.min(aStartTobStart, aEndTobStart, aStartTobEnd, aEndTobEnd)
                switch (shortest) {
                    case aStartTobStart:
                        lineA.geometry.unshift(lineB.geometry[0])
                        break;
                    case aStartTobEnd:
                        lineA.geometry.unshift(lineB.geometry[lineB.geometry.length-1])
                        break;
                    case aEndTobStart:
                        lineA.geometry.push(lineB.geometry[0])
                        break;
                    case aEndTobEnd:
                        lineA.geometry.push(lineB.geometry[lineB.geometry.length-1])
                        break;
                }
            }
        },

        getMergedLine() {
            let mergedGeometry = []
            if (this.roadGeometries.length > 0) {
                this.roadGeometries.forEach(item => {
                    if (mergedGeometry.length < 1) {
                        mergedGeometry = item.geometry
                    } else {
                        // Find the touching point and merge geometry, if needed change geometry order
                        let start = mergedGeometry[0]
                        let end = mergedGeometry[mergedGeometry.length-1]
                        if (getDistance(start, item.geometry[0]) === 0) {
                            item.geometry.reverse()
                            mergedGeometry = [ ...item.geometry, ...mergedGeometry ]
                        } else if (getDistance(start, item.geometry[item.geometry.length-1]) === 0) {
                            mergedGeometry = [...item.geometry, ...mergedGeometry]
                        } else if (getDistance(end, item.geometry[item.geometry.length-1]) === 0) {
                            item.geometry.reverse()
                            mergedGeometry = [...mergedGeometry, ...item.geometry]
                        } else if (getDistance(end, item.geometry[0]) === 0) {
                            mergedGeometry = [...mergedGeometry, ...item.geometry]
                        }
                    }
                })
            }
            return mergedGeometry
        },

        calculateRoadLength() {
            let totalLength = 0
            let lastPoint = undefined
            this.roadGeometries.forEach(item => {
                item.geometry.forEach(item => {
                    if (lastPoint) {
                        totalLength += getDistance(lastPoint, item)
                    }
                    lastPoint = item
                })

            })
            this.roadLength = totalLength
        },

        addPoint(point, id) {
            // Re-draw previous points and set then non-draggable
            if (id > 1) {
                let prevPoint = this.points[id-2]
                let staticIcon = this.map.getMarkerStore().getMeasureDistanceMarkerIcon(this.map.getMapType() === 'LEAFLET')
                this.map.removeMapMarker(id-1, this.MEASURE_ROAD)
                this.map.showMapMarker(id-1, this.MEASURE_ROAD, prevPoint.lat,
                    prevPoint.lng, staticIcon, false)
            }
            // draw new marker that is draggable
            let icon = this.map.getMarkerStore().getNeutralCircleIcon(this.map.getMapType() === 'LEAFLET')
            this.map.showMapMarker(id, this.MEASURE_ROAD, point.lat,
                point.lng, icon, true)
        },

        drawLastRoad() {
            // Draw the last road on the map
            let lastRoad = this.roadGeometries[this.roadGeometries.length-1]
            var xyLine = lastRoad.geometry.map(point => {
                return {x: point.lon, y: point.lat}
            })
            this.map.drawPolyLine(this.roadGeometries.length-1, this.MEASURE_ROAD, xyLine, '#404041', false, false)
        },


    }
}
</script>
<style scoped>
#routa-material-route-map {
    position: relative;
    width: 100%;
    height: 300px;
}
</style>
